import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import moment from "moment-jalaali";
import { Calendar, Clock, User, CalendarOff } from "lucide-react";
import {
  setUserAppointments,
  setUserAppointmentsLoading,
  setUserAppointmentsLoaded,
} from "../../../../redux/action/user/appointments/action";
import { SET_ACCESS_TOKEN } from "../../../../redux/action/auth/type";
import RescheduleVipModal from "../../EditAppointment/RescheduleModal/VipModal";
import RescheduleNormalModal from "../../EditAppointment/RescheduleModal/RegularModal";

const UserAppointments = () => {
  const dispatch = useDispatch();
  const [cookies] = useCookies();

  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const accessToken = useSelector((state) => state.auth.accessToken);
  const { appointments, loading, loaded } = useSelector(
    (state) => state.userAppointments
  );
  const serverTime = useSelector(
    (state) => state.info?.dateNowServer?.serverTime
  );

  // تاریخ امروز به صورت YYYY-MM-DD
  const todayDate = serverTime
    ? serverTime.split("T")[0]
    : moment().format("YYYY-MM-DD");

  const serviceTypeMap = {
    kasht: "کاشت",
    retoosh: "روتوش",
    tarmim: "ترمیم",
    lift: "لیفت",
  };

  // بررسی امکان تغییر زمان (حداقل ۲ روز تا تاریخ قرار)
  const isRescheduleDateAllowed = (appointmentDateStr) => {
    const appDate = new Date(appointmentDateStr + "T00:00:00");
    const today = new Date(todayDate + "T00:00:00");
    const diffTime = appDate.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 2;
  };

  const fetchAppointments = async () => {
    dispatch(setUserAppointmentsLoading(true));
    try {
      const res = await axios.get(
        "https://my-sarad-back.liara.run/api/user/appointments",
        { headers: { authorization: `Bearer ${accessToken}` } }
      );
      dispatch(setUserAppointments(res.data.appointments));
      dispatch(setUserAppointmentsLoaded(true));
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          const refreshRes = await axios.post(
            "https://my-sarad-back.liara.run/api/auth/refresh-token",
            { refreshToken: cookies.refreshToken }
          );
          const newAccess = refreshRes.data.accessToken;
          dispatch({ type: SET_ACCESS_TOKEN, payload: newAccess });

          const retryRes = await axios.get(
            "https://my-sarad-back.liara.run/api/user/appointments",
            { headers: { authorization: `Bearer ${newAccess}` } }
          );
          dispatch(setUserAppointments(retryRes.data.appointments));
          dispatch(setUserAppointmentsLoaded(true));
        } catch (refreshError) {
          toast.error("نشست شما منقضی شده است، لطفا دوباره وارد شوید");
          dispatch(setUserAppointmentsLoaded(true));
        }
      } else {
        toast.error("خطا در دریافت قرارها");
        dispatch(setUserAppointmentsLoaded(true));
      }
    } finally {
      dispatch(setUserAppointmentsLoading(false));
    }
  };

  useEffect(() => {
    if (!loaded) {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const getStatusDisplay = (appointment) => {
    if (appointment.status === "لغو شده")
      return { text: "لغو شده", className: "bg-red-100 text-red-700" };
    if (appointment.status === "درحال پرداخت")
      return { text: "درحال پرداخت", className: "bg-yellow-100 text-yellow-700" };
    if (appointment.status === "تایید شده") {
      if (appointment.datetime.date < todayDate)
        return { text: "استفاده شده", className: "bg-blue-100 text-blue-700" };
      return { text: "رزرو فعال", className: "bg-green-100 text-green-700" };
    }
    return { text: appointment.status, className: "bg-gray-100 text-gray-700" };
  };

  const toPersianNumber = (num) => {
    if (!num) return "";
    return num.toString().replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
  };

  const sortedAppointments = [...appointments].sort((a, b) => {
    if (a.datetime.date < b.datetime.date) return 1;
    if (a.datetime.date > b.datetime.date) return -1;
    if (a.datetime.time < b.datetime.time) return 1;
    if (a.datetime.time > b.datetime.time) return -1;
    return 0;
  });

  const handleRescheduleClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
  };

  const handleRescheduleSuccess = () => {
    handleCloseModal();
    fetchAppointments(); // رفرش لیست
  };

  return (
    <div className="space-y-6 mt-12" dir="rtl">
      <h3 className="text-xl font-bold text-slate-800">رزروهای شما</h3>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : appointments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-500">
          <CalendarOff className="w-16 h-16 text-slate-300" />
          <p className="mt-4 text-lg font-medium">شما هنوز رزروی ندارید</p>
          <p className="text-sm">
            با رزرو اولین وقت، برنامه‌تان را در اینجا ببینید
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
          {sortedAppointments.map((appointment) => {
            const statusInfo = getStatusDisplay(appointment);
            const isVip = appointment.service.method === "VIP";
            const isActive =
              appointment.status === "تایید شده" &&
              appointment.datetime.date >= todayDate &&
              isRescheduleDateAllowed(appointment.datetime.date);

            return (
              <div
                key={appointment._id}
                className="p-4 bg-white justify-between rounded-xl shadow-md border border-slate-100 flex flex-col gap-3"
              >
                <div className="flex justify-between flex-wrap items-center">
                  {isVip ? (
                    <div className="flex items-center gap-2 text-slate-700 font-semibold">
                      <User className="w-4 h-4 text-purple-500" />
                      <span>{appointment.staff.displayForUser}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-600">
                      <span className="font-medium">
                        {appointment.service.name}
                      </span>
                      <span className="mx-1">•</span>
                      <span>{serviceTypeMap[appointment.service.type]}</span>
                      <span
                        className={`mr-2 pr-0.5 px-1.5 font-iransans py-0.5 rounded text-xs ${
                          isVip
                            ? "bg-amber-100 text-amber-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {" "}
                        {appointment.service.method}{" "}
                      </span>
                    </div>
                  )}
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${statusInfo.className}`}
                  >
                    {statusInfo.text}
                  </span>
                </div>

                {isVip && (
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">
                      {appointment.service.name}
                    </span>
                    <span className="mx-1">•</span>
                    <span>{serviceTypeMap[appointment.service.type]}</span>
                    <span
                      className={`mr-2 pr-0.5 px-1.5 font-iransans py-0.5 rounded text-xs ${
                        isVip
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {" "}
                      {appointment.service.method}{" "}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-sm text-slate-500">
                  <span className="flex items-center gap-1 font-bold">
                    <Calendar className="w-4 h-4" />
                    {toPersianNumber(
                      moment(appointment.datetime.date, "YYYY-MM-DD").format(
                        "jYYYY/jMM/jDD"
                      )
                    )}
                  </span>
                  <span className="flex items-center gap-1 font-bold">
                    <Clock className="w-4 h-4" />
                    {toPersianNumber(appointment.datetime.time)}
                  </span>
                </div>

                <button
                  disabled={!isActive}
                  onClick={() => handleRescheduleClick(appointment)}
                  className={`mt-2 flex items-center justify-center gap-2 w-full h-10 rounded-lg text-xs font-medium shadow transition ${
                    isActive
                      ? "text-[#7a3e3e] bg-[#fbd4c8] hover:opacity-90 cursor-pointer"
                      : "opacity-50 bg-slate-200 text-slate-600 cursor-not-allowed"
                  }`}
                >
                  تغییر زمان رزرو
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* مودال تغییر زمان */}
      {showModal && selectedAppointment && (
        <>
          {selectedAppointment.service.method === "VIP" ? (
            <RescheduleVipModal
              appointment={selectedAppointment}
              onClose={handleCloseModal}
              onSuccess={handleRescheduleSuccess}
            />
          ) : (
            <RescheduleNormalModal
              appointment={selectedAppointment}
              onClose={handleCloseModal}
              onSuccess={handleRescheduleSuccess}
            />
          )}
        </>
      )}
    </div>
  );
};



export default UserAppointments;